// src/platform/DatasetCoveragePanel.jsx
import {
    useEffect,
    useState,
    useImperativeHandle,
    forwardRef,
} from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import PlatformOperatingAoiMap from "./components/PlatformOperatingAoiMap";
import { formatAcquisitionDate } from "../layers/imagery/formatImageryDate";

// Delegates to the canonical formatter. This was a hand-rolled copy of it,
// one of SEVEN, and the day-precision change had to be made in each — see
// layers/imagery/formatImageryDate.js. Only the empty-value contract stays
// local, because callers differ on what a missing date should read as.
function formatAcqDate(d) {
    return formatAcquisitionDate(d);
}

function formatAreaKm2(area_m2) {
    if (area_m2 == null) return "—";
    return `${(Number(area_m2) / 1e6).toLocaleString(undefined, {
        maximumFractionDigits: 0,
    })} km²`;
}

function formatAreaSqMi(area_m2) {
    if (area_m2 == null) return "—";
    const sqmi = Number(area_m2) / 2_589_988.110336;
    return `${sqmi.toLocaleString(undefined, {
        maximumFractionDigits: 0,
    })} sq mi`;
}

const DatasetCoveragePanel = forwardRef(function DatasetCoveragePanel(_, ref) {
    const [datasets, setDatasets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    // ----------------------------
    // LOAD LIST
    // ----------------------------
    async function loadDatasets(selectId = null) {
        setLoading(true);
        setError(null);

        try {
            const res = await fetchWithAuth(
                "/api/v1/platform/datasets/imagery"
            );
            if (!res.ok) throw new Error("Failed to load datasets");

            const data = await res.json();
            const list = Array.isArray(data.datasets) ? data.datasets : [];

            setDatasets(list);

            setSelectedId(prev => {
                if (selectId && list.some(d => d.id === selectId)) return selectId;
                if (prev && list.some(d => d.id === prev)) return prev;
                return list.length > 0 ? list[0].id : null;
            });
        } catch (e) {
            console.error(e);
            setError("Failed to load datasets");
            setDatasets([]);
            setSelectedId(null);
        } finally {
            setLoading(false);
        }
    }

    // ----------------------------
    // LOAD FOOTPRINT GEOMETRY (MAP)
    // Returns { id, geom } — same shape PlatformOperatingAoiMap consumes
    // for operating AOIs, so the map component is reused unchanged.
    // ----------------------------
    async function loadDatasetFootprint(datasetId) {
        const res = await fetchWithAuth(
            `/api/v1/platform/datasets/${datasetId}/footprint?lod=fine`
        );
        if (!res.ok) {
            throw new Error("Failed to load dataset footprint");
        }
        return await res.json();
    }

    // ----------------------------
    // IMPERATIVE API (parity with OperatingAoisPanel)
    // ----------------------------
    useImperativeHandle(ref, () => ({
        reload: (selectId) => loadDatasets(selectId),
    }));

    // ----------------------------
    // INITIAL LOAD
    // Datasets are global (not org-scoped) — no activeOrgId gating.
    // ----------------------------
    useEffect(() => {
        loadDatasets();
    }, []);

    // ----------------------------
    // RENDER
    // ----------------------------
    return (
        <div className="flex h-full">
            {/* LEFT COLUMN */}
            <div className="w-80 shrink-0 border-r border-bg-border bg-bg-soft p-4 flex flex-col">
                <h2 className="text-sm font-semibold">Dataset Coverage</h2>
                <p className="mt-2 text-xs text-gray-400">
                    Imagery dataset footprints. Select a dataset to see its
                    coverage on the map.
                </p>

                <div className="mt-4 flex-1 overflow-auto">
                    {loading && (
                        <div className="flex justify-center pt-6">
                            <Loader2 className="animate-spin text-gray-400" />
                        </div>
                    )}

                    {error && (
                        <div className="pt-4 text-sm text-red-400">
                            {error}
                        </div>
                    )}

                    {!loading && !error && datasets.length === 0 && (
                        <div className="pt-4 text-sm text-fg-secondary">
                            No imagery datasets found.
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        datasets.map(ds => (
                            <button
                                key={ds.id}
                                onClick={() => setSelectedId(ds.id)}
                                className={`w-full text-left px-1 py-1 mb-0 rounded border border-transparent
                                    ${selectedId === ds.id
                                        ? "bg-accent-action/20 border-accent-action/40 text-orange-300"
                                        : "hover:bg-bg text-gray-300"
                                    }`}
                            >
                                <div className="text-[11px] truncate">
                                    {ds.slug}
                                </div>
                                <div className="text-[11px] text-fg-secondary mt-1 flex justify-between">
                                    <span>{formatAcqDate(ds.acquisition_date)}</span>
                                    <span>
                                        {ds.resolution_cm != null
                                            ? `${ds.resolution_cm} cm`
                                            : ""}
                                    </span>
                                </div>
                                <div className="text-[11px] text-fg-secondary mt-0.5">
                                    <span>{ds.sku_display_name ?? ds.sku_code ?? "Empty"} - </span>
                                    <span>{formatAreaSqMi(ds.area_m2)}</span>
                                </div>
                            </button>
                        ))}
                </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="flex-1 bg-black flex flex-col">
                <PlatformOperatingAoiMap
                    aois={datasets}
                    selectedId={selectedId}
                    loadAoiGeometry={loadDatasetFootprint}
                />
            </div>
        </div>
    );
});

export default DatasetCoveragePanel;